import { Injectable } from '@angular/core';

import { Fixture } from '@cds/betting-offer';
import { TagCount } from '@cds/betting-offer/tags';
import { NextToGoConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { toDictionary } from '@frontend/sports/common/core/utils/collection';
import { NumberDictionary, StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { EventFactory } from '@frontend/sports/grid/core/feature/factories';
import { GridEvent } from '@frontend/sports/grid/core/feature/model';
import { entries, groupBy, min, orderBy, sortBy, sumBy } from 'lodash-es';
import { Observable, of } from 'rxjs';

import { TabBarItem } from '../tab-bar/tab-bar.models';

export enum NextToGoTabs {
    All = 0,
}

@Injectable()
export class NextToGoService {
    constructor(
        private nextToGoConfig: NextToGoConfig,
        private eventFactory: EventFactory,
        private sitecore: Sitecore,
    ) {}

    private get fixtureOrder(): NumberDictionary<number> {
        const ids = this.nextToGoConfig.module.topCompetitionsOrder.concat(this.nextToGoConfig.module.participantIds);

        return toDictionary(
            ids,
            (id) => id,
            (_, index) => index,
        );
    }

    buildEvents(fixtures?: Fixture[], sports?: TagCount[]): Observable<GridEvent[]> {
        const gridEvents: GridEvent[] = [];
        const sportsOrder = toDictionary(
            sports,
            (sport) => sport.tag.id,
            (_, index) => index,
        );

        if (fixtures) {
            const sortedFixtures = this.sortFixtures(fixtures, sportsOrder);

            return this.eventFactory.create(sortedFixtures);
        }

        return of(gridEvents);
    }

    marketGridGroupIds(): NumberDictionary<string> {
        const groupIds: NumberDictionary<string> = {};

        this.nextToGoConfig.module.sportDetails.map((sportDetail) => {
            groupIds[sportDetail.sportId] = sportDetail.gridGroupId;
        });

        return groupIds;
    }

    private sortFixtures(fixtures: Fixture[], sportsOrder: NumberDictionary<number>): Fixture[] {
        const sortedFixtures: Fixture[] = [];

        const fixturesByDate = groupBy(fixtures, (fixture) => fixture.startDate) as StringDictionary<Fixture[]>;
        const groupedByDateKeyValuesPair = entries(fixturesByDate);

        groupedByDateKeyValuesPair.map((dateWithFixtures) => {
            if (dateWithFixtures[1].length > 1) {
                const groupedBySports = groupBy(dateWithFixtures[1], (fixture) => fixture.sport.id);

                const sportWithFixtures = entries(groupedBySports);
                const orderedSportWithFixtures = sortBy(sportWithFixtures, (sportFixture) => sportsOrder[sportFixture[0] as unknown as number]);

                orderedSportWithFixtures.forEach((item) => {
                    const result = orderBy(item[1], [(fixture) => this.getOrder(fixture), (fixture) => fixture.name.value]);
                    sortedFixtures.push(...result);
                });
            } else {
                sortedFixtures.push(...dateWithFixtures[1]);
            }
        });

        return sortedFixtures;
    }

    mapSportTabs(sports: TagCount[] | undefined): TabBarItem[] {
        if (!sports?.length) {
            return [];
        }

        let tabs: TabBarItem[] = [];
        const totalEvents = sumBy(sports, (sport) => sport.preMatch);

        if (sports.length > 1) {
            tabs.push({
                icon: 'sports-all',
                title: this.sitecore.globalMessages.AllSports,
                url: '',
                id: NextToGoTabs.All,
                count: totalEvents,
            });
        }

        tabs = tabs.concat(
            sports.map((sport) => ({
                id: sport.tag.id,
                title: sport.tag.name.value,
                icon: `sports-${sport.tag.id}`,
                count: sport.preMatch,
            })),
        );

        return tabs;
    }

    private getOrder(fixture: Fixture): number {
        const orderingIds = fixture.participants.map((p) => p.participantId);

        if (fixture.competition) {
            orderingIds.push(fixture.competition.id);
        }

        const orderedIndexes = orderingIds.map((id) => this.fixtureOrder[id]);

        return min(orderedIndexes)!;
    }
}
